'use client';

import { cn } from '@/lib/utils';
import { Skeleton } from '@mui/material';
import type { MarketEventRelateMarket } from '@/components/predic/ui/MarketEvent';
import { useEffect, useMemo } from 'react';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useTranslation } from '@/lib/i18n';
import { range } from 'lodash';
import FixedChatWindow from '../../message/FixedChatWindow';
import PythraSummary from '../../ui/PythraSummary';
import MarketSocailSignal from './MarketSocailSignal';
import TwitterList from './TwitterList';
import NewsTimeline, { createNewsTimelineGroups } from '../../ui/NewsTimeline';
import {
  useEventStreaming,
  type EventStreamingData,
  type UseEventStreamingOptions,
} from '@/hooks/use-event-streaming';
import { MarketCard, type MarketCardMarket } from '../../cards/market';
import { findDisplayMarket } from '../../utils/market';

export interface MarketTopicResponse {
  success: boolean;
  data: EventStreamingData;
}

export default function MarketTopic({ id, position }: { id?: string; position?: 'right' }) {
  const { openDrawer } = useRightDrawer();
  const { t } = useTranslation();

  const eventStreamingOptions = useMemo<UseEventStreamingOptions>(
    () =>
      position === 'right'
        ? { eventId: id, datas: ['event', 'market', 'social_signal', 'twitter'] }
        : { eventId: id },
    [id, position],
  );

  const { event, news, relateMarket, isLoading } = useEventStreaming(eventStreamingOptions);
  const newsTimelineGroups = useMemo(() => createNewsTimelineGroups(news), [news]);
  const twitters = useMemo(() => event?.twitter?.filter((t) => !!t.username), [event?.twitter]);
  const relatedMarketList = useMemo(
    () => relateMarket.map(toRelatedMarketCardItem),
    [relateMarket],
  );

  const poster =
    event?.image_url || news.flatMap((n) => n.events).find((e) => e?.image)?.image || null;

  useEffect(() => {
    if (newsTimelineGroups.length > 0 && position !== 'right') {
      openDrawer(<NewsTimeline groups={newsTimelineGroups} />, t('Live Updates'), true);
    }
  }, [openDrawer, t, newsTimelineGroups, position]);

  // useUnmount(() => closeDrawer());

  if (isLoading) {
    return <LoadingHolder />;
  }

  return (
    <>
      <div className="max-w-245 mx-auto pb-25">
        <div
          className="rounded-xl w-full h-61.5 mb-8 bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: poster ? `url(${poster})` : undefined }}
        >
          <div className="p-5 bg-linear-to-t from-black/60 to-transparent h-full flex flex-col items-start gap-4">
            {event?.category && (
              <span className="rounded-md text-sm px-2 py-1 bg-white text-black">
                {event.category}
              </span>
            )}
            <h2 className="text-[40px] leading-14 font-bold line-clamp-3">{event?.title}</h2>
          </div>
        </div>
        <PythraSummary summary={event?.description} loading={isLoading} />
        <MarketSocailSignal socialSignal={event?.social_signal || undefined} />
        <TwitterList twitter={twitters} />
        <div className="leading-6 font-semibold my-5">{t('Related Markets')}</div>
        <div className="space-y-4">
          {relatedMarketList.map((item) => (
            <MarketCard
              key={item.id}
              marketEventId={item.id}
              market={item.market}
              href={item.href}
            />
          ))}
        </div>
      </div>
      {!isLoading && position !== 'right' && (
        <FixedChatWindow
          suggestions={event?.recommended_questions || []}
          sourceId={`event_${event?.event_id}`}
          userContext={
            event
              ? JSON.stringify({
                  context_type: 'market_topic',
                  data: { ...event, trending_card: event },
                })
              : undefined
          }
        />
      )}
    </>
  );
}

interface RelatedMarketCardItem {
  id: string;
  href: string;
  market: MarketCardMarket;
}

function toRelatedMarketCardItem(data: MarketEventRelateMarket): RelatedMarketCardItem {
  const eventSlug = data.slug || data.event_id || data.id;

  return {
    id: data.event_id || data.id,
    href: `/predic/market?id=${eventSlug}`,
    market: toMarketCardData(data),
  };
}

function toMarketCardData(data: MarketEventRelateMarket): MarketCardMarket {
  const displayMarket = findDisplayMarket(data.markets);
  const eventTitle = data.title || displayMarket?.market_question || '';
  const poster =
    data.image ||
    data.icon ||
    displayMarket?.market_icon ||
    displayMarket?.market_image ||
    '/image/polymarket.svg';
  const yesPrice = toNumber(displayMarket?.outcome_yes_price);
  const change24h = toNumber(displayMarket?.price_change_24h);
  const volume24h = toNumber(data.volume_24hr || 0);
  const groupItemTitle = displayMarket?.group_item_title || displayMarket?.outcome_yes || 'YES';
  const isEnded = data.closed === 1;

  return {
    marketEventId: data.event_id || data.id,
    imageSrc: poster,
    imageAlt: eventTitle,
    title: eventTitle,
    outcome: groupItemTitle,
    probability: yesPrice * 100,
    change: change24h * 100,
    changePeriod: '(24h)',
    volume: volume24h,
    category: data.category || data.time_category || '',
    resolvedOutcome: isEnded ? (yesPrice > 0.5 ? 'YES' : 'NO') : undefined,
  };
}

function toNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}

function LoadingHolder() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto">
      <div className="rounded-xl w-full h-61.5 mb-8">
        <Skeleton variant="rounded" sx={{ bgcolor: 'grey.800', height: '246px' }} />
      </div>
      <div
        className="border border-[rgba(255,255,255,.08)] rounded-xl p-4 mt-8 space-y-2"
        style={{
          background: 'linear-gradient(101.67deg, #201841 3.82%, #291229 97.83%)',
        }}
      >
        <Skeleton variant="rounded" width={100} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" sx={{ bgcolor: 'grey.800' }} />
      </div>
      <div className="leading-6 font-semibold my-5">{t('Related Markets')}</div>
      <MarketLoading />
    </div>
  );
}

export function MarketLoading() {
  return (
    <div className="space-y-4">
      {range(4).map((item) => (
        <div
          key={item}
          className={cn(
            'rounded-lg overflow-hidden w-full hover:opacity-90 transition-opacity',
            'py-4 px-3 space-y-3',
            'bg-linear-to-b from-[#202020] to-[#0B0B0B]',
          )}
        >
          <div className="flex items-center gap-2.5">
            <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: 'grey.800' }} />
            <div className="flex-1">
              <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
              <div className="text-xs flex items-center gap-2">
                <Skeleton
                  variant="text"
                  width={60}
                  sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }}
                />
                <Skeleton
                  variant="text"
                  width={60}
                  sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }}
                />
                <Skeleton
                  variant="text"
                  width={60}
                  sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
